import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { UploadSimple, FileDoc, CircleNotch, Plus } from '@phosphor-icons/react';

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const toIso = (s) => {
  if (!s) return '';
  const t = String(s).trim();
  let m = t.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) { let [, d, mo, y] = m; if (y.length === 2) y = `20${y}`; return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`; }
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  return '';
};

export function PssrImportButton({ onImported }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" data-testid="pssr-import-open" onClick={() => setOpen(true)}>
        <UploadSimple size={16} weight="bold" className="mr-1.5" />Importer un PSSR (Word)
      </Button>
      {open && <PssrImportDialog onClose={() => setOpen(false)} onImported={onImported} />}
    </>
  );
}

function PssrImportDialog({ onClose, onImported }) {
  const navigate = useNavigate();
  const [parsing, setParsing] = useState(false);
  const [items, setItems] = useState(null);
  const [target, setTarget] = useState('existing');
  const [mocs, setMocs] = useState([]);
  const [mocId, setMocId] = useState('');
  const [sites, setSites] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newSite, setNewSite] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    mocApi.listMocs().then((m) => setMocs(Array.isArray(m) ? m : []));
    mocApi.listSites().then((s) => { const a = Array.isArray(s) ? s : []; setSites(a); if (a[0]) setNewSite(a[0].Site_ID); });
  }, []);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setParsing(true); setItems(null);
    const res = await mocApi.parsePssrDocx(file);
    setParsing(false);
    if (!res.ok) { toast.error(res.error || 'Lecture impossible'); return; }
    const parsed = (res.data.items || []).map((it) => ({
      ...it,
      responsable: it.responsable || '',
      echeance: toIso(it.delai) || addDays(30),
      blocking: it.phase === 'before',
      include: true,
    }));
    if (parsed.length === 0) toast.warning('Aucune action détectée dans ce document.');
    setItems(parsed);
  };

  const setRow = (i, patch) => setItems((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const doImport = async () => {
    const selected = (items || []).filter((x) => x.include);
    if (selected.length === 0) { toast.error('Aucune action sélectionnée.'); return; }
    let targetId = mocId;
    setImporting(true);
    if (target === 'new') {
      if (!newTitle.trim() || !newSite) { setImporting(false); toast.error('Titre et site requis pour la nouvelle MOC.'); return; }
      const created = await mocApi.createMoc({ title: newTitle.trim(), Site_ID: newSite, nature: 'Permanent', currentSituation: 'Créée par import PSSR', futureSituation: newTitle.trim(), department: '' });
      if (!created.ok) { setImporting(false); toast.error(created.error || 'Création de la MOC impossible'); return; }
      targetId = created.id;
    } else if (!targetId) { setImporting(false); toast.error('Choisissez une MOC de destination.'); return; }

    let ok = 0; let fail = 0;
    for (const it of selected) {
      const r = await mocApi.createAction({
        MOC_ID: targetId,
        Description: it.action,
        Responsable_ID: it.responsable || '',
        Echeance: it.echeance || addDays(30),
        Origine: 'PSSR',
        Obligatoire_Avant_Demarrage: !!it.blocking,
      });
      if (r.ok) ok += 1; else fail += 1;
    }
    setImporting(false);
    toast.success(`${ok} action(s) importée(s)${fail ? `, ${fail} échec(s)` : ''}`);
    if (onImported) onImported();
    onClose();
    if (targetId) navigate(`/moc/${targetId}`);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pssr-import-dialog">
        <DialogHeader><DialogTitle>Importer un PSSR (Word)</DialogTitle></DialogHeader>

        {!items && (
          <div className="py-6 text-center">
            <label className="inline-flex flex-col items-center gap-3 cursor-pointer">
              <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
                {parsing ? <CircleNotch size={26} className="animate-spin text-primary" /> : <FileDoc size={26} weight="bold" className="text-primary" />}
              </div>
              <span className="text-sm text-muted-foreground">{parsing ? 'Lecture du document…' : 'Sélectionnez votre fichier PSSR .docx'}</span>
              <input data-testid="pssr-import-file" type="file" accept=".docx" className="hidden" onChange={onFile} disabled={parsing} />
              {!parsing && <span className="text-xs px-3 py-1.5 border border-border rounded">Choisir un fichier</span>}
            </label>
            <p className="text-xs text-muted-foreground mt-4 max-w-md mx-auto">On lit les tableaux « Actions avant mise en route » et « Actions après mise en route ». La qualité dépend de la mise en page du Word.</p>
          </div>
        )}

        {items && (
          <div className="space-y-4">
            <div className="text-sm font-medium">{items.length} action(s) détectée(s) — vérifiez et ajustez :</div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} data-testid={`pssr-import-item-${i}`} className={`border rounded-sm p-3 ${it.include ? 'border-border' : 'border-dashed border-border opacity-60'}`}>
                  <div className="flex items-start gap-2">
                    <input type="checkbox" className="accent-primary h-4 w-4 mt-1" checked={it.include} onChange={(e) => setRow(i, { include: e.target.checked })} data-testid={`pssr-import-include-${i}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{it.action}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${it.phase === 'before' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'}`}>{it.phase === 'before' ? 'Avant démarrage' : 'Après démarrage'}</span>
                        {it.reception && <span className="text-xs text-muted-foreground">Réception : {it.reception}</span>}
                      </div>
                      <div className="grid sm:grid-cols-2 gap-2 mt-2">
                        <Input value={it.responsable} onChange={(e) => setRow(i, { responsable: e.target.value })} placeholder="Responsable (e-mail)" className="h-8 text-sm" data-testid={`pssr-import-owner-${i}`} />
                        <Input type="date" value={it.echeance} onChange={(e) => setRow(i, { echeance: e.target.value })} className="h-8 text-sm" data-testid={`pssr-import-due-${i}`} />
                      </div>
                      {it.delai && <div className="text-[11px] text-muted-foreground mt-1">Délai d'origine : <b>{it.delai}</b> — ajustez la date ci-dessus.</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border pt-3 space-y-3">
              <div className="text-sm font-semibold">Rattacher à :</div>
              <div className="flex gap-2">
                <button type="button" data-testid="pssr-target-existing" onClick={() => setTarget('existing')} className={`text-sm px-3 py-1.5 rounded border ${target === 'existing' ? 'border-primary bg-primary/5 font-medium' : 'border-border text-muted-foreground'}`}>Une MOC existante</button>
                <button type="button" data-testid="pssr-target-new" onClick={() => setTarget('new')} className={`text-sm px-3 py-1.5 rounded border ${target === 'new' ? 'border-primary bg-primary/5 font-medium' : 'border-border text-muted-foreground'}`}>Nouvelle MOC</button>
              </div>
              {target === 'existing' ? (
                <Select value={mocId} onValueChange={setMocId}>
                  <SelectTrigger data-testid="pssr-import-moc-select" className="h-9"><SelectValue placeholder="Choisir une MOC…" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {mocs.map((m) => <SelectItem key={m.id} value={m.id}>{m.id} — {m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input data-testid="pssr-import-new-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Titre de la nouvelle MOC" className="h-9" />
                  <Select value={newSite} onValueChange={setNewSite}>
                    <SelectTrigger data-testid="pssr-import-new-site" className="h-9"><SelectValue placeholder="Site" /></SelectTrigger>
                    <SelectContent>{sites.map((s) => <SelectItem key={s.Site_ID} value={s.Site_ID}>{s.Nom_Site || s.Site_ID}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          {items && (
            <Button data-testid="pssr-import-confirm" disabled={importing} onClick={doImport}>
              {importing ? <CircleNotch size={15} className="animate-spin mr-1.5" /> : <Plus size={15} weight="bold" className="mr-1.5" />}
              Importer les actions
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
